import FilterOutlined from '@ant-design/icons/FilterOutlined'
import { Input, Skeleton } from 'antd'
import { fuzzyMatch } from 'features/libra/helpers/fuzzyMatch'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflows } from 'features/libra/ticket/libraTicketsApi'
import { useRolodexEntries } from 'features/rolodex'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

type AssetListViewProps = {
  tickets: ReadonlyArray<LibraTicket>
  selectedAssetId?: string
  setSelectedAssetId: Dispatch<SetStateAction<string | undefined>>
  isLoading: boolean
}

type AssetStats = {
  unitId: string
  unitName: string
  ticketCount: number
  avgProgress: number
}

export const AssetListView: FC<AssetListViewProps> = ({
  tickets,
  selectedAssetId,
  setSelectedAssetId,
  isLoading,
}) => {
  const [filterText, setFilterText] = useState('')

  // Get unique unit IDs
  const unitIds = useMemo(
    () =>
      Array.from(
        new Set(
          tickets
            .map((ticket) => ticket.tags.unit)
            .filter((unit): unit is string => unit !== undefined)
        )
      ),
    [tickets]
  )

  // Query units
  const { entries: unitEntries, isLoading: unitsLoading } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.UNIT,
        ids: unitIds,
      }),
      [unitIds]
    )
  )

  const unitMap = useMemo(
    () => new Map(unitEntries.map((u) => [u.id, u])),
    [unitEntries]
  )

  // Get workflow IDs from tickets
  const workflowIds = useMemo(() => {
    return Array.from(
      new Set(
        tickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      )
    )
  }, [tickets])

  const { libraWorkflows } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((w) => [w.id, w])),
    [libraWorkflows]
  )

  // Calculate stats for each asset
  const assetStats = useMemo<ReadonlyArray<AssetStats>>(() => {
    const statsMap = new Map<string, AssetStats>()

    tickets.forEach((ticket) => {
      const unitId = ticket.tags.unit
      if (!unitId) return

      const unit = unitMap.get(unitId)
      const unitName = (unit?.data.name as string) || unitId

      if (!statsMap.has(unitId)) {
        statsMap.set(unitId, {
          unitId,
          unitName,
          ticketCount: 0,
          avgProgress: 0,
        })
      }

      const stats = statsMap.get(unitId)!
      stats.ticketCount++

      // Calculate progress
      const workflow = workflowMap.get(ticket.workflowId)
      if (workflow) {
        const currentIndex = workflow.stepIndices.get(ticket.stepId) ?? -1
        const totalSteps = workflow.steps.length
        if (ticket.workflowComplete) {
          stats.avgProgress += 100
        } else if (currentIndex >= 0) {
          stats.avgProgress += (currentIndex / totalSteps) * 100
        }
      }
    })

    // Calculate averages
    statsMap.forEach((stats) => {
      if (stats.ticketCount > 0) {
        stats.avgProgress = Math.round(stats.avgProgress / stats.ticketCount)
      }
    })

    return Array.from(statsMap.values()).sort((a, b) =>
      a.unitName.localeCompare(b.unitName)
    )
  }, [tickets, unitMap, workflowMap])

  const filteredAssets = useMemo(() => {
    const query = filterText.trim().toLowerCase()
    if (!query) return assetStats
    return assetStats.filter((asset) => fuzzyMatch(asset.unitName, query))
  }, [assetStats, filterText])

  const isBusy = isLoading || unitsLoading

  return (
    <Container>
      <Header>
        <Title>Assets</Title>
        <Count>{assetStats.length}</Count>
      </Header>
      <FilterContainer>
        <Input
          placeholder='Filter assets'
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          allowClear
          prefix={<FilterIcon />}
          size='small'
        />
      </FilterContainer>
      <AssetList>
        {isBusy ? (
          <SkeletonContainer>
            <Skeleton active title={false} paragraph={{ rows: 8 }} />
          </SkeletonContainer>
        ) : (
          filteredAssets.map((asset) => (
            <AssetCard
              key={asset.unitId}
              $isSelected={selectedAssetId === asset.unitId}
              onClick={() => setSelectedAssetId(asset.unitId)}>
              <AssetName>{asset.unitName}</AssetName>
              <AssetStats>
                <StatBadge>{asset.ticketCount} tickets</StatBadge>
                <ProgressBadge $progress={asset.avgProgress}>
                  {asset.avgProgress}% complete
                </ProgressBadge>
              </AssetStats>
            </AssetCard>
          ))
        )}
        {!isBusy && filteredAssets.length === 0 && (
          <EmptyState>No assets found</EmptyState>
        )}
      </AssetList>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const Count = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background: ${({ theme }) => theme.colors.gray[700]};
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
`

const FilterContainer = styled.div`
  padding: 0.75rem 1rem;
`

const FilterIcon = styled(FilterOutlined)`
  color: ${({ theme }) => theme.colors.gray[700]};
`

const AssetList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 0.5rem;
`

const SkeletonContainer = styled.div`
  padding: 1rem;
`

const AssetCard = styled.div<{ $isSelected: boolean }>`
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.background3 : theme.colors.gray[800]};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

const AssetName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`

const AssetStats = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const StatBadge = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background: ${({ theme }) => theme.colors.gray[700]};
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
`

const ProgressBadge = styled.span<{ $progress: number }>`
  font-size: 0.75rem;
  color: ${({ $progress }) =>
    $progress > 70 ? '#52c41a' : $progress > 30 ? '#faad14' : '#ff4d4f'};
  background: ${({ theme }) => theme.colors.gray[700]};
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-weight: 500;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`
